/**
 * Teacher → Announcements (read + simple compose).
 *
 * Phase 1: list announcements visible to / authored by this teacher.
 * Full compose UI with audience targeting + channels (WhatsApp/SMS) lives
 * in /admin → Communications — link to it from the header.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Megaphone, Loader2, ExternalLink } from 'lucide-react';
import { portalApi } from './teacherApi';

const fmt = (d) => d ? new Date(d).toLocaleString() : '';

export const TeacherAnnouncements = ({ tenantId }) => {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['teacher-announcements'],
    queryFn: () => portalApi('/teacher/announcements'),
  });

  return (
    <div className="space-y-3" data-testid="teacher-announcements-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-indigo-500" /> Announcements <Badge variant="outline">{items.length}</Badge>
        </h2>
        <Button variant="outline" size="sm" asChild>
          <a href={`/admin/schools/${tenantId}#communications`} target="_blank" rel="noopener noreferrer">
            Composer (Admin) <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
          </a>
        </Button>
      </div>

      {isLoading ? (
        <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-slate-500 p-8 border border-dashed rounded">
          No announcements visible.  Compose one via the Admin Communications console.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <Card key={a.id} data-testid={`teacher-announcement-${a.id}`}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-sm">{a.title || a.subject || '(no title)'}</h3>
                      {a.audience && <Badge variant="outline" className="text-[10px]">{a.audience}</Badge>}
                      {a.channels?.length && <Badge variant="outline" className="text-[10px]">{a.channels.join(' · ')}</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap line-clamp-3">{a.body || a.message || ''}</p>
                    <p className="text-xs text-slate-400 mt-1.5">{fmt(a.created_at)} · {a.author_name || a.author_id || 'system'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherAnnouncements;
